import { useState, useMemo } from 'react';
import {
  Card, CardContent, Grid, Stack, Chip, Typography, Table, TableHead, TableRow, TableCell, TableBody, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, Snackbar, Box, LinearProgress,
} from '@mui/material';
import { Payments, WarningAmber, AccountBalanceWallet } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { ListFilters, matchFilters, EMPTY_FILTERS, type FilterValue } from '../../components/ListFilters';
import { dealerPaymentsApi, type DealerPayment, type MarkPaidInput } from '../../api/dealerPayments.api';
import { dealersApi } from '../../api/dealers.api';
import { extractApiError } from '../../api/error';

const inr = (x = 0) => `₹${Number(x).toLocaleString('en-IN')}`;
const PAY_MODES = ['neft', 'rtgs', 'upi', 'cheque', 'cash', 'adjustment'];
const statusColor = (s: string): 'default' | 'success' | 'warning' | 'error' => (s === 'reconciled' ? 'success' : s === 'disputed' ? 'error' : 'warning');
const statusLabel = (s: string) => (s === 'reconciled' ? 'Paid' : s === 'disputed' ? 'Disputed' : 'Unpaid');

function ageingDays(inv: DealerPayment): number | null {
  if (inv.reconciliationStatus === 'reconciled' || !inv.dueDate) return null;
  return Math.floor((Date.now() - new Date(`${inv.dueDate}T00:00:00`).getTime()) / 86400000);
}

/**
 * Ageing Report — receivables-focused dealer ledger: every dealer invoice with
 * paid / outstanding, ageing (days overdue vs due date) and status, sorted most-
 * overdue first. Internal team can acknowledge a payment via "Payment Received".
 * (Distinct from Dealer Payments, which is the full finance hub with charts.)
 */
export function AgeingReportPage() {
  const qc = useQueryClient();
  const [payFor, setPayFor] = useState<DealerPayment | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterValue>({ ...EMPTY_FILTERS });

  const dash = useQuery({ queryKey: ['dealer-finance-dash'], queryFn: () => dealerPaymentsApi.reconciliation() });
  const invoices = useQuery({ queryKey: ['dealer-invoices'], queryFn: () => dealerPaymentsApi.list({ entryType: 'invoice', limit: 500 }) });
  const dealers = useQuery({ queryKey: ['dealers', 'names'], queryFn: () => dealersApi.list({ limit: 500 }) });
  const dealerName = (id: number) => (dealers.data?.data.find((d) => d.id === id)?.dealerName) ?? `Dealer #${id}`;
  const refresh = () => { qc.invalidateQueries({ queryKey: ['dealer-finance-dash'] }); qc.invalidateQueries({ queryKey: ['dealer-invoices'] }); };
  const accept = useMutation({ mutationFn: (id: number) => dealerPaymentsApi.accept(id), onSuccess: () => { setToast('Invoice accepted'); refresh(); }, onError: (e) => setToast(extractApiError(e)) });

  const k = dash.data;
  const rows = useMemo(() => {
    const list = (invoices.data?.data ?? []).filter((i) => matchFilters(filters, { text: `${dealerName(i.dealerId)} ${i.paymentCode}`, status: i.reconciliationStatus, date: i.dueDate, dealerId: i.dealerId }));
    return [...list].sort((a, b) => (ageingDays(b) ?? -9999) - (ageingDays(a) ?? -9999));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices.data, dealers.data, filters]);

  return (
    <>
      <PageHeader title="Ageing Report" subtitle="Receivables ageing — dealer-wise outstanding, ageing buckets & status." />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><Mini color="#1e40af" icon={<AccountBalanceWallet />} label="Outstanding" value={inr(k?.outstandingAmount)} /></Grid>
        <Grid item xs={6} md={3}><Mini color="#dc2626" icon={<WarningAmber />} label="Overdue" value={inr(k?.overdueAmount)} /></Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}><CardContent>
            <Typography variant="caption" color="text.secondary">Outstanding Ageing Buckets</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {(k?.ageing ?? []).map((a) => <Chip key={a.label} label={`${a.label}: ${inr(a.value)}`} color={a.label === '90+' ? 'error' : a.label === 'current' ? 'default' : 'warning'} variant={a.value > 0 ? 'filled' : 'outlined'} />)}
              {(k?.ageing ?? []).length === 0 && <Typography variant="caption" color="text.secondary">No data yet</Typography>}
            </Stack>
          </CardContent></Card>
        </Grid>
      </Grid>

      <ListFilters value={filters} onChange={setFilters} statusOptions={['unreconciled', 'reconciled', 'disputed']}
        show={['search', 'status', 'dealer', 'month', 'dateFrom', 'dateTo']} />

      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Dealer Ledger — Reconciliation &amp; Ageing</Typography>
          {invoices.isLoading && <LinearProgress />}
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Dealer</TableCell><TableCell>Invoice</TableCell><TableCell align="right">Amount</TableCell><TableCell align="right">Paid</TableCell>
                <TableCell align="right">Outstanding</TableCell><TableCell>Due</TableCell><TableCell>Ageing</TableCell><TableCell>Status</TableCell><TableCell align="right">Action</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {rows.map((inv) => {
                  const paid = inv.reconciliationStatus === 'reconciled' ? inv.amount : (inv.amountPaid ?? 0);
                  const outstanding = Math.max(0, inv.amount - paid);
                  const days = ageingDays(inv);
                  return (
                    <TableRow key={inv.id} hover>
                      <TableCell>{dealerName(inv.dealerId)}</TableCell>
                      <TableCell>{inv.paymentCode}</TableCell>
                      <TableCell align="right">{inr(inv.amount)}</TableCell>
                      <TableCell align="right">{inr(paid)}</TableCell>
                      <TableCell align="right">{inr(outstanding)}</TableCell>
                      <TableCell>{inv.dueDate ?? '—'}</TableCell>
                      <TableCell>{days == null ? '—' : days > 0 ? <Chip size="small" color="error" label={`${days}d overdue`} /> : <span style={{ color: '#64748b' }}>{days === 0 ? 'due today' : `${-days}d to due`}</span>}</TableCell>
                      <TableCell><Chip size="small" color={statusColor(inv.reconciliationStatus)} label={statusLabel(inv.reconciliationStatus)} /></TableCell>
                      <TableCell align="right">
                        {!inv.acceptedAt && <Button size="small" onClick={() => accept.mutate(inv.id)}>Accept</Button>}
                        {inv.reconciliationStatus !== 'reconciled' && <Button size="small" variant="outlined" startIcon={<Payments />} onClick={() => setPayFor(inv)}>Payment Received</Button>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && <TableRow><TableCell colSpan={9}><Box py={3}><Typography variant="body2" color="text.secondary" textAlign="center">No invoices.</Typography></Box></TableCell></TableRow>}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Card>

      {payFor && <PaymentReceivedDialog invoice={payFor} onClose={() => setPayFor(null)} onDone={() => { setPayFor(null); setToast('Payment recorded'); refresh(); }} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function Mini({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string; color: string }) {
  return (
    <Card sx={{ background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`, color: '#fff', borderRadius: 2 }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
        <Box><Typography variant="h6" fontWeight={800} lineHeight={1.1}>{value}</Typography><Typography variant="caption" sx={{ opacity: 0.9 }}>{label}</Typography></Box>
      </CardContent>
    </Card>
  );
}

function PaymentReceivedDialog({ invoice, onClose, onDone }: { invoice: DealerPayment; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState<MarkPaidInput>({ paymentDate: new Date().toISOString().slice(0, 10), paymentMode: 'upi', amountPaid: invoice.amount });
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof MarkPaidInput>(k: K, v: MarkPaidInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const mut = useMutation({ mutationFn: () => dealerPaymentsApi.markPaid(invoice.id, form), onSuccess: onDone, onError: (e) => setError(extractApiError(e)) });
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Payment Received — {invoice.paymentCode}</DialogTitle>
      <Alert severity="info" sx={{ mx: 3 }}>Acknowledge a payment received from the dealer (use when the dealer hasn’t updated it).</Alert>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={2}>
            <TextField label="Payment Date" type="date" InputLabelProps={{ shrink: true }} value={form.paymentDate} onChange={(e) => set('paymentDate', e.target.value)} fullWidth />
            <TextField select label="Payment Mode" value={form.paymentMode} onChange={(e) => set('paymentMode', e.target.value)} fullWidth>{PAY_MODES.map((m) => <MenuItem key={m} value={m}>{m.toUpperCase()}</MenuItem>)}</TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Amount Paid" type="number" value={form.amountPaid} onChange={(e) => set('amountPaid', Number(e.target.value))} fullWidth />
            <TextField label="UTR Number" value={form.utrNumber ?? ''} onChange={(e) => set('utrNumber', e.target.value)} fullWidth />
          </Stack>
          <TextField label="Remarks" value={form.remarks ?? ''} onChange={(e) => set('remarks', e.target.value)} multiline minRows={2} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={mut.isPending} onClick={() => mut.mutate()}>Record Payment</Button>
      </DialogActions>
    </Dialog>
  );
}
